import { faCircleInfo, faUsersViewfinder } from "@fortawesome/pro-regular-svg-icons"
import { faTags } from "@fortawesome/pro-solid-svg-icons"
import { Divider, Typography } from "@mui/material"
import { LOCALES } from "app/language/model"
import { languageSVC, translate } from "app/language/service"
import { IRootState } from "app/model"
import { PageLoading } from "page/components/PageLoading"
import React from "react"
import { connect } from "react-redux"
import { ContentBox } from "ui/components/ContentBox"
import { FlexBox } from "ui/components/FlexBox"
import { addImportant } from "ui/tools/common"
import { isTabletScreen } from "ui/tools/responsive"
import { setTheme } from "ui/tools/theme"
import { UserTags } from "user-tag/components/UserTags"
import { getUser, getUserInfo, setUserInfo } from "user/tools/common"
import { isImpersonating } from "user/tools/impersonate"
import { canSavePasswordSelf, canSaveUserSelf } from "user/tools/permissions"
import { AvailableImpersonators } from "./AvailableImpersonators"
import { GivenImpersonators } from "./GivenImpersonators"
import { UserForm } from "./UserForm"

interface IMyProfilePageProps {
  isInitializating: boolean
  isCurrentlyImpersonating: boolean
  isTablet: boolean
}

const MyProfilePageFn = ({
  isInitializating,
  isCurrentlyImpersonating,
  isTablet
}: IMyProfilePageProps): JSX.Element => {
  const [user, setUser] = React.useState(getUser())
  return (
    <FlexBox fullWidth jc="start" vertical fullHeight>
      {isInitializating ? (
        <PageLoading
          id="my-profil-is-initializing"
          title={translate("label.loading.data")}
          sx={{ mt: "2rem", mb: "2rem" }}
        />
      ) : (
        <FlexBox jc="start" ai="start" fullWidth vertical gap sx={{ p: "1rem", height: "100%", overflow: "auto" }}>
          <FlexBox jc="start" ai="start" fullWidth vertical>
            <ContentBox
              id="user-detail-content-box"
              icon={faCircleInfo}
              title={translate("label.details")}
              vertical
              sxChildren={
                !isTablet
                  ? {
                      "& > *": {
                        alignItems: addImportant("start")
                      }
                    }
                  : undefined
              }
              defaultClosed>
              <UserForm
                onSave={updatedUser => {
                  const hasLocaleChanged = updatedUser.language_id !== user.language_id
                  setUser(updatedUser)
                  setUserInfo({ ...getUserInfo(), user: updatedUser })
                  setTheme(updatedUser.ui_settings.theme === "dark")
                  if (hasLocaleChanged) {
                    languageSVC.setLocale(updatedUser.language_id as LOCALES)
                  }
                }}
                otherUsers={[]} // better to pass users but not necessary
                userId={user.id}
                disableUserEdit={!canSaveUserSelf()}
                disableUserPassword={!canSavePasswordSelf()}
                disableUserRole
              />
            </ContentBox>
            <ContentBox
              id="user-impersonate-content-box"
              icon={faUsersViewfinder}
              title={translate("user.impersonate.title")}
              vertical
              defaultClosed>
              {isCurrentlyImpersonating ? (
                <Typography color="textPrimary" fontSize=".9rem">
                  {translate("user.impersonate.again.denied")}
                </Typography>
              ) : (
                <>
                  <AvailableImpersonators />
                  <Divider sx={{ width: "100%" }} />
                  <GivenImpersonators />
                </>
              )}
            </ContentBox>
            <ContentBox
              id="user-tags-content-box"
              icon={faTags}
              title={translate("page.tags.title")}
              vertical
              defaultClosed>
              <UserTags />
            </ContentBox>
          </FlexBox>
        </FlexBox>
      )}
    </FlexBox>
  )
}

export const MyProfilePage = connect(
  (state: IRootState): IMyProfilePageProps => ({
    isInitializating: state.app.isInitializating,
    isCurrentlyImpersonating: isImpersonating(state.user),
    isTablet: isTabletScreen(state)
  })
)(MyProfilePageFn)
